import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  discoverEntitiesForPartner,
  getPartnerMappingRules,
  createSliceGroupWithSlice,
  CreateSliceGroupParams,
  EntityDiscovered,
  EntityConfig,
  PartnerMappingRules
} from '../../../../../mogul/core/_requests'
import { APIError } from '../../../../../app/modules/auth'
import usePartners from '../../../../../mogul/hooks/usePartners'
import { formatLeadItemDateBetter } from '../DebugPipedrive'

interface EntityDiscoveryProps {
  partnerId?: number
}

export function EntityDiscovery({ partnerId }: EntityDiscoveryProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [entities, setEntities] = useState<EntityDiscovered[]>([])
  const [entityConfig, setEntityConfig] = useState<EntityConfig | null>(null)
  const [mappingRules, setMappingRules] = useState<PartnerMappingRules | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lookbackHours, setLookbackHours] = useState(168) // 7 days
  const [creatingSliceFor, setCreatingSliceFor] = useState<string | null>(null)
  const [confirmingSliceFor, setConfirmingSliceFor] = useState<string | null>(null)

  const { partners, loading: partnersLoading } = usePartners(true)
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | undefined>(partnerId)

  // Debug logging
  React.useEffect(() => {
    console.log('EntityDiscovery - Partners loaded:', partners?.length, 'Partners:', partners)
    if (partners?.length > 0) {
      console.log('First partner details:', {
        id: partners[0].id,
        name: partners[0].name,
        unique_identifier: partners[0].unique_identifier,
        id_type: typeof partners[0].id,
        id_isNaN: isNaN(partners[0].id)
      })

      const validPartners = partners.filter(partner => partner.id && !isNaN(partner.id))
      console.log('Valid partners after filtering:', validPartners.length, validPartners)
    }
  }, [partners])

  const loadEntities = useCallback(async () => {
    if (!selectedPartnerId) return

    setLoading(true)
    setError(null)
    try {
      // Load both entity data and mapping rules
      const [entitiesResult, rulesResult] = await Promise.all([
        discoverEntitiesForPartner(selectedPartnerId, lookbackHours),
        getPartnerMappingRules(selectedPartnerId)
      ])

      setEntities(entitiesResult.entities)
      setEntityConfig(entitiesResult.entity_config)
      setMappingRules(rulesResult)
    } catch (err) {
      console.error('Entity discovery error:', err)
      const errorMessage = err instanceof APIError ? err.message : 'Failed to discover entities'
      setError(errorMessage)
      setEntities([])
      setEntityConfig(null)
      setMappingRules(null)
    } finally {
      setLoading(false)
    }
  }, [selectedPartnerId, lookbackHours])

  // Auto-load entities when partner is selected or lookback period changes
  useEffect(() => {
    if (selectedPartnerId) {
      loadEntities()
    }
  }, [selectedPartnerId, lookbackHours, loadEntities])

  const createSliceGroup = async (entity: EntityDiscovered) => {
    if (!entityConfig || !mappingRules) return

    // Generate slice group name based on partner's template
    const sliceGroupName = entityConfig.slice_group_name_template.replace('{{entity_name}}', entity.name)

    setCreatingSliceFor(entity.unique_identifier)
    setConfirmingSliceFor(null)

    try {
      const params: CreateSliceGroupParams = {
        partnerId: selectedPartnerId!, // We know this is set because button is disabled otherwise
        accountId: 2, // Premier account ID - TODO: make this configurable
        funeralHomeName: entity.name, // This field name is legacy but works for any entity
        funeralHomeIdentifier: entity.unique_identifier,
        sliceGroupName: sliceGroupName
      }

      const result = await createSliceGroupWithSlice(params)

      if (result.status === 'success') {
        // Navigate to the slice settings page with a success notice
        const sliceId = result.slice.unique_identifier
        navigate(`/debugging/all-leads/webhooks/slices/${sliceId}/settings`, {
          state: {
            justCreated: true,
            entityName: entity.name,
            entityType: entityConfig.display_name
          }
        })
      } else {
        alert(`Failed to create slice: ${result.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error creating slice group:', error)
      alert(`Error creating slice: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setCreatingSliceFor(null)
      setConfirmingSliceFor(null)
    }
  }

  // Get display names based on partner's entity config
  const entityDisplayName = entityConfig?.display_name || 'Entity'
  const entityDisplayNamePlural = entityConfig?.display_name_plural || 'Entities'
  const discoveryType = mappingRules?.discovery_type || 'custom'

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">{entityDisplayNamePlural} Discovery</h3>
        <div className="card-toolbar">
          <button
            className="btn btn-sm btn-primary"
            onClick={loadEntities}
            disabled={loading || !selectedPartnerId}
          >
            {loading ? 'Loading...' : `Refresh ${entityDisplayNamePlural}`}
          </button>
        </div>
      </div>

      <div className="card-body">
        <div className="row mb-5">
          <div className="col-md-6">
            <label className="form-label">Partner</label>
            <select
              className="form-select"
              value={selectedPartnerId || ''}
              onChange={(e) => setSelectedPartnerId(e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">{partnersLoading ? 'Loading partners...' : 'Select Partner'}</option>
              {!partnersLoading && partners.filter(partner => partner.id && !isNaN(partner.id)).map(partner => (
                <option key={partner.unique_identifier} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Lookback Period</label>
            <select
              className="form-select"
              value={lookbackHours}
              onChange={(e) => setLookbackHours(parseInt(e.target.value))}
            >
              <option value={24}>Last 24 hours</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
              <option value={336}>Last 2 weeks</option>
              <option value={720}>Last 30 days</option>
              <option value={1440}>Last 2 months</option>
              <option value={2160}>Last 3 months</option>
            </select>
          </div>
        </div>

        {mappingRules && (
          <div className="alert alert-info mb-5">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Discovery Type:</strong> {discoveryType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            {discoveryType === 'agent_based' && ' (showing agents who have sent leads)'}
            {discoveryType === 'funeral_home_based' && ' (showing funeral homes that have sent leads)'}
            {discoveryType === 'custom' && ' (showing entities based on webhook data)'}
          </div>
        )}

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {!selectedPartnerId && (
          <div className="alert alert-info">
            <i className="fas fa-search me-2"></i>
            Select a partner to discover {entityDisplayNamePlural.toLowerCase()} that have sent webhook data recently.
          </div>
        )}

        {entities.length > 0 && (
          <>
            <div className="row mb-5">
              <div className="col-md-3">
                <div className="card border border-gray-300">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-gray-800">{entities.length || 0}</div>
                    <div className="text-muted">Total {entityDisplayNamePlural}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border border-warning">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-warning">{entities.filter(e => !e.has_slice).length || 0}</div>
                    <div className="text-muted">Need Setup</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border border-success">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-success">{entities.filter(e => e.has_slice).length || 0}</div>
                    <div className="text-muted">Configured</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border border-danger">
                  <div className="card-body text-center">
                    <div className="fw-bold fs-2 text-danger">{entities.filter(e => !e.has_slice && e.webhook_count >= 50).length || 0}</div>
                    <div className="text-muted">High Priority</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-row-dashed">
                <thead>
                  <tr>
                    <th>{entityDisplayName}</th>
                    <th>Recent Activity</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entities.map((entity, idx) => {
                    const priority = entity.webhook_count >= 50 ? 'high' : entity.webhook_count >= 10 ? 'medium' : 'low'
                    const priorityColor = priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'secondary'

                    return (
                      <tr key={idx} className={[
                        !entity.has_slice ? 'table-warning' : '',
                        confirmingSliceFor === entity.unique_identifier ? 'bg-light-primary' : ''
                      ].filter(Boolean).join(' ')}>
                        <td>
                          <div className="d-flex align-items-center">
                            {!entity.has_slice && (
                              <i className="fas fa-exclamation-triangle text-warning me-2" title="Needs Setup"></i>
                            )}
                            <div>
                              <div className="fw-bold">{entity.name || `Unnamed ${entityDisplayName}`}</div>
                              <div className="text-muted small">
                                <code>{entity.unique_identifier}</code>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <span className={`badge badge-light-${priorityColor} me-2`}>
                              {entity.webhook_count} webhooks
                            </span>
                            {priority === 'high' && (
                              <span className="badge badge-light-danger me-2">High Priority</span>
                            )}
                            {entity.last_webhook_at && (
                              <div className="text-muted small">
                                Last: {formatLeadItemDateBetter(entity.last_webhook_at)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {entity.has_slice ? (
                            <span className="badge badge-light-success">
                              <i className="fas fa-check me-1"></i>
                              Configured {entity.slice_group_name && `(${entity.slice_group_name})`}
                            </span>
                          ) : (
                            <span className="badge badge-light-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Needs Setup
                            </span>
                          )}
                        </td>
                        <td>
                          {!entity.has_slice && (
                            <>
                              {confirmingSliceFor === entity.unique_identifier ? (
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => createSliceGroup(entity)}
                                    disabled={creatingSliceFor === entity.unique_identifier}
                                  >
                                    {creatingSliceFor === entity.unique_identifier ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Creating...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-check me-1"></i>
                                        Confirm
                                      </>
                                    )}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-light"
                                    onClick={() => setConfirmingSliceFor(null)}
                                    disabled={creatingSliceFor === entity.unique_identifier}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => setConfirmingSliceFor(entity.unique_identifier)}
                                >
                                  <i className="fas fa-plus me-1"></i>
                                  Create Slice
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {selectedPartnerId && !loading && entities.length === 0 && !error && (
          <div className="alert alert-warning">
            <i className="fas fa-search me-2"></i>
            No {entityDisplayNamePlural.toLowerCase()} found for the selected timeframe. Try increasing the lookback period or check if webhooks are being received.
          </div>
        )}
      </div>
    </div>
  )
}